import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { liveMoversURL } from '../api/client';
import { LiveMoversMessageSchema, type MoversResponse } from '../api/types';
import type { DashboardFilters } from '../lib/filters';

interface LiveState {
  isLive: boolean;
  lastMessageAt: Date | null;
  error: string | null;
}

export function useLiveMovers(filters: DashboardFilters): LiveState {
  const queryClient = useQueryClient();
  const [state, setState] = useState<LiveState>({ isLive: false, lastMessageAt: null, error: null });
  const reconnectAttempt = useRef(0);

  useEffect(() => {
    let closed = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: number | undefined;

    function connect() {
      if (closed) return;
      try {
        socket = new WebSocket(liveMoversURL(filters));
      } catch (error) {
        onFailure(error);
        return;
      }

      socket.onopen = () => {
        reconnectAttempt.current = 0;
        setState((prev) => ({ ...prev, isLive: true, error: null }));
      };

      socket.onmessage = (event) => {
        try {
          const parsed = LiveMoversMessageSchema.parse(JSON.parse(String(event.data)));
          queryClient.setQueryData<MoversResponse>(['movers', filters], {
            window: parsed.window,
            items: parsed.items,
          });
          if (parsed.stats) queryClient.setQueryData(['stats'], parsed.stats);
          setState({ isLive: true, lastMessageAt: new Date(parsed.timestamp), error: null });
        } catch (error) {
          setState((prev) => ({
            ...prev,
            error: error instanceof Error ? error.message : 'Malformed live feed message',
          }));
        }
      };

      socket.onerror = () => onFailure(new Error('Live WebSocket error'));
      socket.onclose = () => {
        setState((prev) => ({ ...prev, isLive: false }));
        scheduleReconnect();
      };
    }

    function onFailure(error: unknown) {
      setState((prev) => ({
        ...prev,
        isLive: false,
        error: error instanceof Error ? error.message : 'Live connection failed',
      }));
      scheduleReconnect();
    }

    function scheduleReconnect() {
      if (closed) return;
      window.clearTimeout(reconnectTimer);
      reconnectAttempt.current += 1;
      const delay = Math.min(15_000, 750 * 2 ** Math.min(reconnectAttempt.current, 5));
      reconnectTimer = window.setTimeout(connect, delay);
    }

    connect();

    return () => {
      closed = true;
      window.clearTimeout(reconnectTimer);
      socket?.close(1000, 'filter changed');
    };
  }, [queryClient, filters]);

  return state;
}
